<template>
  <v-container class="pa-0 mb-8 payment-container">
    <div v-if="loading" class="d-flex justify-center pt-8">
      <v-progress-circular indeterminate />
    </div>
    <div v-else>
      <div class="px-4 py-6">
        <h2>{{ $t('kiosk.terminalCommands.title') }}</h2>
      </div>
      <LayoutCard :title="$t('kiosk.terminalCommands.x.title')" rounded>
        <p>
          {{ $t('kiosk.terminalCommands.x.description') }}
        </p>
        <Button
          dark
          :disabled="reportDisabled"
          :loading="loadingReport"
          @click="report"
        >
          {{ $t('kiosk.terminalCommands.x.cta') }}
          <v-icon right color="white">mdi-playlist-check</v-icon>
        </Button>
      </LayoutCard>
      <LayoutCard :title="$t('kiosk.terminalCommands.z.title')" rounded>
        <p>
          {{ $t('kiosk.terminalCommands.z.description') }}
        </p>
        <Button
          dark
          :disabled="settleDisabled"
          :loading="loadingSettle"
          @click="settle"
        >
          {{ $t('kiosk.terminalCommands.z.cta') }}
          <v-icon right color="white">mdi-bank</v-icon>
        </Button>
      </LayoutCard>
      <LayoutCard
        :title="
          $t('kiosk.terminalCommands.unresolvedCRNs.cardTitle', {
            count: unresolvedCRNs.length,
          })
        "
        rounded
      >
        <p>
          {{ $t('kiosk.terminalCommands.unresolvedCRNs.cardDescription') }}
        </p>
        <Button
          dark
          :disabled="voidDisabled"
          :loading="loadingVoid"
          @click="voidUnresolvedPayments"
        >
          {{ $t('kiosk.terminalCommands.unresolvedCRNs.cta') }}
          <v-icon right color="white">mdi-transfer</v-icon>
        </Button>
      </LayoutCard>
      <LayoutCard
        :title="$t('kiosk.terminalCommands.connections.title')"
        rounded
      >
        <div class="d-flex mb-2 justify-space-between">
          {{ $t('kiosk.terminalCommands.connections.cardReaderLabel') }}
          <v-chip
            :color="terminalOnline ? 'green' : 'red'"
            text-color="white"
            @click="reconnectToCardReader"
          >
            {{ terminalOnline ? $t('generic.ok') : $t('generic.unavailable') }}
          </v-chip>
        </div>
        <div class="d-flex mb-2 justify-space-between">
          {{ $t('kiosk.terminalCommands.connections.printerLabel') }}
          <v-chip
            :color="printerOnline ? 'green' : 'red'"
            text-color="white"
            @click="tryToConnectToPrinter"
          >
            {{ printerOnline ? $t('generic.ok') : $t('generic.unavailable') }}
          </v-chip>
        </div>
        <div class="d-flex justify-space-between">
          {{ $t('kiosk.terminalCommands.connections.testPrinterLabel') }}
          <v-chip
            dark
            :disabled="!printerOnline"
            text-color="white"
            @click="testPrinter"
          >
            {{ $t('kiosk.terminalCommands.connections.print') }}
          </v-chip>
        </div>
      </LayoutCard>
    </div>
    <v-dialog v-model="dialog.open" width="500" persistent>
      <v-card>
        <v-card-title class="text-h5 grey lighten-2">
          {{ dialog.title }}
        </v-card-title>

        <v-card-text class="pt-5" style="white-space: pre-line">
          {{ dialog.text }}
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="dialog.open = false">
            {{ $t('generic.close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import Button from '@/components/Button.vue';
import LayoutCard from '@/components/LayoutCard.vue';
import { OPERATION, generateCRN, ecrNameVersion } from '@/utils/worldLine';

export default {
  components: {
    Button,
    LayoutCard,
  },
  data() {
    return {
      loading: true,
      loadingReport: false,
      loadingSettle: false,
      loadingVoid: false,
      paymentIntent: {},
      dialogText: null,
      dialog: {
        open: false,
        text: '',
        title: '',
      },
      settlementCRN: null,
      printerInterval: null,
    };
  },
  computed: {
    terminalSocketConnected() {
      return this.$store.getters.terminalSocketConnected;
    },
    printerOnline() {
      return this.$store.getters.printerIsOnline;
    },
    terminalOnline() {
      return this.$store.getters.terminalOnline;
    },
    settleDisabled() {
      return (
        !this.terminalSocketConnected ||
        !this.terminalOnline ||
        this.loadingSettle ||
        !this.printerOnline ||
        this.loadingReport
      );
    },
    reportDisabled() {
      return (
        !this.terminalSocketConnected ||
        !this.terminalOnline ||
        this.loadingReport ||
        !this.printerOnline ||
        this.loadingSettle
      );
    },
    voidDisabled() {
      return (
        !this.terminalSocketConnected ||
        !this.terminalOnline ||
        this.loadingVoid ||
        !this.printerOnline ||
        this.loadingReport ||
        this.loadingSettle
      );
    },
    unresolvedCRNs() {
      return this.$store.getters.unresolvedCRNs || [];
    },
  },
  async mounted() {
    const sessionToken = this.$route.params.session;
    try {
      await this.$store.dispatch('fetchSessionData', { session: sessionToken });
    } catch (error) {
      console.error('TERMINAL: Failed to fetch session data.', error);
    }
    this.loading = false;

    // A failing terminal connection must not block the screen — the
    // connection chips already reflect the socket state reactively.
    await this.$store.dispatch('subscribeToPosTerminal');
    this.setTerminalMessageCallback();
  },
  methods: {
    async settle() {
      const CRN = this.settlementCRN || generateCRN();

      this.loadingSettle = true;
      this.settlementCRN = CRN;

      try {
        await this.$store.dispatch('voidUnresolvedCRNs');

        const data = {
          operation: OPERATION.SETTLEMENT,
          ecrNameVersion,
          CRN,
          dlgDisabled: false,
          receiptLineLength: 40,
        };
        this.$store.dispatch('sendPosTerminalMessage', { data });
      } catch (error) {
        console.error(
          'TERMINAL: Failed to resolve unresolved CRNs before settlement.',
          error
        );
      }
    },
    report() {
      this.loadingReport = true;
      const data = {
        operation: OPERATION.REPORT,
        CRN: generateCRN(),
      };
      this.$store.dispatch('sendPosTerminalMessage', { data });
    },
    async voidUnresolvedPayments() {
      this.loadingVoid = true;
      const onVoided = () => {
        this.loadingVoid = false;
      };
      try {
        await this.$store.dispatch('voidUnresolvedCRNs', { onVoided });
      } finally {
        this.loadingVoid = false;
      }
    },
    setTerminalMessageCallback() {
      const onmessage = async message => {
        const data = JSON.parse(message.data);
        if (data.operation === OPERATION.SETTLEMENT && data.result) {
          this.$store.dispatch('saveSettlement', {
            data,
          });

          const onPrintSuccess = () => {
            this.dialog.title = this.$t('kiosk.terminalCommands.z.title');
            this.dialog.text = data.merchantReceipt;
            this.dialog.open = true;
            this.loadingSettle = false;

            // IMPORTANT to reset the CRN after a successful settlement
            this.settlementCRN = null;
          };
          const onPrinterError = () => {
            this.dialog.title = this.$t('kiosk.terminalCommands.z.title');
            this.dialog.text = this.$t('kiosk.terminalCommands.printerError');
            this.dialog.open = true;
            this.loadingSettle = false;
          };

          this.printReceipt(
            data.merchantReceipt,
            onPrintSuccess,
            onPrinterError
          );

          return;
        } else if (data.operation === OPERATION.REPORT && data.result) {
          const onSuccessfulReport = () => {
            this.dialog.title = this.$t('kiosk.terminalCommands.x.title');
            this.dialog.text = data.merchantReceipt;
            this.dialog.open = true;
            this.loadingReport = false;
          };

          const onFailedReport = () => {
            this.dialog.title = this.$t('kiosk.terminalCommands.x.title');
            this.dialog.text = this.$t('kiosk.terminalCommands.printerError');
            this.dialog.open = true;
            this.loadingReport = false;
          };

          this.printReceipt(
            data.merchantReceipt,
            onSuccessfulReport,
            onFailedReport
          );

          return;
        } else if (data.operation === OPERATION.VOID && data.result) {
          this.printReceipt(data.cardholderReceipt);

          await this.$store.dispatch('updateInPersonPaymentIntent', {
            type: 'payment_voided',
            data,
          });

          this.$store.dispatch('removeUnresolvedCRN', { CRN: data.CRN });

          return;
        } else if (
          data.operation === OPERATION.VOID &&
          !data.result &&
          data.responseCode === 'NF'
        ) {
          console.error('TERMINAL: CRN was not found, nothing to auto-void.');
          this.$store.dispatch('removeUnresolvedCRN', { CRN: data.CRN });
        } else if (!data.result && data.responseCode === 'NP') {
          console.log('WORLDLINE: SALE returned NP, retrying silently...');
          this.$store.dispatch('setTerminalOffline');
          return;
        } else if (!data.result && data.responseCode) {
          this.dialog.title = `Error (${data.responseCode})`;
          this.dialog.text = data.responseText;
          this.dialog.open = true;

          this.loadingReport = false;
          this.loadingSettle = false;

          return;
        }
      };
      this.$store.dispatch('setOnMessageCallback', { callback: onmessage });
    },
    tryToConnectToPrinter() {
      this.$store.dispatch('connectToPOSPrinter');
    },
    async testPrinter() {
      const additionalFields = [
        {
          name: 'Vardas',
          placeholder: 'Jūsų vardas',
          value: 'Vardenis',
        },
        {
          name: 'Vieta',
          placeholder: 'Pasirinkite vietą',
          value: 'Išsinešimui',
        },
      ];
      const text = `               Greet menu
   J. Basanavičiaus g. 41-6, LT-03109
                Vilnius
       TID=9999928; MID=TST99928
      Contactless Payment Receipt
                  SALE
TERMINAL ID                     TST99928
MERCHANT ID                      9999928
TIME                 11.08.2026 16:51:47
BATCH 260811/739            RECEIPT 5359
VISA                    Visa Contactless
************0605
A0000000031010               Visa Credit
RRN 622316005359
Baltic POS Tetra 1.0.001 (0041)
Amount                         24.60 EUR
Authorization No 046304              T:1`;
      const items = [
        {
          name:
            'Cheeseburger with fries and a drink combo meal with a very long name that should wrap to the next line',
          quantity: 2,
          price: 5.5,
          modifiers: [
            { name: 'Extra cheese', price: 0.5 },
            { name: 'No onions', price: 0 },
          ],
        },
        // Diacritics on purpose: an ASCII-only sample cannot reveal a printer
        // code page that drops glyphs and knocks the price column out of line.
        {
          name: 'Šaltibarščiai su bulvėmis ir grietine',
          quantity: 1,
          price: 2.5,
          modifiers: [{ name: 'Be svogūnų', price: 0 }],
        },
      ];
      await this.$store.dispatch('printReceipt', {
        text,
        withLogo: true,
        orderId: '28',
        QRLink: 'https://greet.menu',
        additionalFields,
        items,
        discount: 2,
        discountCode: 'SUMMER20',
      });
    },
    async reconnectToCardReader() {
      await this.$store.dispatch('subscribeToPosTerminal');
      this.setTerminalMessageCallback();
    },
    async printReceipt(text, onSuccess, onError) {
      await this.$store.dispatch('printReceipt', {
        text,
        withLogo: false,
        onSuccess,
        onError,
      });
    },
  },
};
</script>
